(function () {
  'use strict';

  // ============================================================
  // BRAND CONFIG — the values most likely to change per brand.
  // See README.md in this folder for the full checklist.
  // ============================================================
  const BRAND = {
    fontBody: '"Roboto Condensed", sans-serif',
    fontHeading: '"Roboto Condensed", sans-serif',
    fontWeightHeading: '700',
    colorText: '#1C1C1C',
    colorBorder: '#d8d5d1',
    colorBg: '#EFEFEF',
    accent: '#044879', // check-icon + asterisk-link color; distinct from colorText for brands with a separate accent
    radius: '0px', // container/panel corner radius — 0 for sharp-cornered brands, higher for rounded ones
    titleTextTransform: 'uppercase',
    titleLetterSpacing: '1.5px',
    // Verified live against russellathletic.com.au/pages/shipping-delivery on
    // 2026-09-14 — Russell Athletic's real threshold/rates differ from
    // Jockey's: $79 (not $90), and no membership gate on free shipping.
    freeShippingThreshold: '$79+',
    deliveryBusinessDays: 3, // editorial estimate for the trust-box date, not a published guarantee — real published range is 3-7 business days (standard)
    returnsWindowDays: 30,
    returnsPolicyUrl: 'https://russellathletic.com.au/pages/returns-exchanges',
    shippingOptionsUrl: 'https://russellathletic.com.au/pages/shipping-delivery',
  };

  // Retries a single DOM query-and-bail against late theme-JS hydration —
  // running from document-start means DOMContentLoaded can fire before the
  // buy box / accordion have finished rendering, unlike the old
  // document-idle timing this feature originally shipped under.
  function pocRetryUntil(check, { tries = 20, interval = 250 } = {}) {
    let attempts = 0;
    (function tick() {
      if (check() || ++attempts >= tries) return;
      setTimeout(tick, interval);
    })();
  }

  function init() {
    injectTrustBoxStyle();
    injectLightboxStyle();
    pocRetryUntil(() => {
      injectTrustBox();
      return !!document.getElementById('poc-trust-box');
    });
    pocRetryUntil(() => {
      rewriteShippingReturnsPanel();
      return !!document.getElementById('poc-shipping-returns-copy');
    });
  }

  // ---------- Trust points box ----------

  const TRUST_BOX_CSS = `
    #poc-trust-box{
      display: flex;
      font-family: ${BRAND.fontBody};
      margin-top: 8px;
      border: 1px solid ${BRAND.colorBorder};
      background: ${BRAND.colorBg};
      border-radius: ${BRAND.radius};
      overflow: hidden;
    }
    #poc-trust-box .poc-trust-cell{
      display: flex;
      align-items: center;
      /* flex-start (not center) lines every icon up at the same starting
         position regardless of how much its own text wraps — see
         jockey-pdp-suite.user.js v1.3.1 history in POCs.md for why. */
      justify-content: flex-start;
      gap: 8px;
      flex: 1;
      min-width: 0;
      padding: 5px 8px;
      border-left: 1px solid ${BRAND.colorBorder};
    }
    #poc-trust-box .poc-trust-cell:first-child{ border-left: none; }
    #poc-trust-box .poc-trust-icon{
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      border-radius: 0;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #poc-trust-box .poc-trust-icon svg{
      color: ${BRAND.accent};
    }
    #poc-trust-box .poc-trust-text{
      min-width: 0;
      text-align: left;
    }
    #poc-trust-box .poc-trust-title{
      font-size: 13px;
      font-family: ${BRAND.fontHeading};
      font-weight: ${BRAND.fontWeightHeading};
      color: ${BRAND.colorText};
      line-height: 1.2;
      text-transform: ${BRAND.titleTextTransform};
      letter-spacing: ${BRAND.titleLetterSpacing};
    }
    #poc-trust-box .poc-trust-detail{
      font-size: 12px;
      color: ${BRAND.colorText};
      line-height: 1.25;
    }
    #poc-trust-box .poc-trust-asterisk-link{
      color: inherit;
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
    }
    @media (max-width: 360px){
      #poc-trust-box{ flex-direction: column; }
      #poc-trust-box .poc-trust-cell{
        border-left: none;
        border-top: 1px solid ${BRAND.colorBorder};
        padding: 7px 10px;
      }
      #poc-trust-box .poc-trust-cell:first-child{ border-top: none; }
    }
  `;

  const CHECK_ICON =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 7"/></svg>';

  function injectTrustBoxStyle() {
    if (document.getElementById('poc-trust-box-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-trust-box-style';
    style.textContent = TRUST_BOX_CSS;
    document.head.appendChild(style);
  }

  function injectTrustBox() {
    // RA's live theme renders Add to Cart/Buy Now inside a <buy-buttons> custom
    // element (not Jockey's .payment-buttons div) — verified live 2026-09-18.
    const paymentButtons = document.querySelector('buy-buttons');
    if (!paymentButtons || document.getElementById('poc-trust-box')) return;
    // Anchor to the block-level buttons container so the new block renders on
    // its own line below, instead of inside <buy-buttons>' own flex row.
    const buttonRow = paymentButtons.closest('.product-form__buttons-container') || paymentButtons;

    const box = document.createElement('div');
    box.id = 'poc-trust-box';

    const columns = [
      {
        title: 'Free shipping',
        detail: `on orders ${BRAND.freeShippingThreshold}`,
      },
      {
        title: 'Delivery',
        detail: `by ${formatDeliveryDate()}`,
      },
      {
        title: 'Easy returns',
        detail: `${BRAND.returnsWindowDays}-day returns and exchanges`,
        asterisk: true,
      },
    ];

    columns.forEach((col) => {
      const cell = document.createElement('div');
      cell.className = 'poc-trust-cell';

      const icon = document.createElement('div');
      icon.className = 'poc-trust-icon';
      icon.innerHTML = CHECK_ICON;

      const text = document.createElement('div');
      text.className = 'poc-trust-text';

      const title = document.createElement('div');
      title.className = 'poc-trust-title';
      title.appendChild(document.createTextNode(col.title));

      const detail = document.createElement('div');
      detail.className = 'poc-trust-detail';
      detail.appendChild(document.createTextNode(col.detail));
      if (col.asterisk) {
        // The asterisk itself opens the returns policy in the same lightbox
        // used by the accordion links below, instead of being dead punctuation.
        const asteriskLink = document.createElement('a');
        asteriskLink.href = BRAND.returnsPolicyUrl;
        asteriskLink.className = 'poc-trust-asterisk-link';
        asteriskLink.textContent = '*';
        asteriskLink.setAttribute('aria-label', 'Returns and exchange policy details');
        interceptAsLightbox(asteriskLink, 'Returns & exchanges');
        detail.appendChild(asteriskLink);
      }

      text.appendChild(title);
      text.appendChild(detail);
      cell.appendChild(icon);
      cell.appendChild(text);
      box.appendChild(cell);
    });

    buttonRow.insertAdjacentElement('afterend', box);
  }

  // Adds N business days (Mon-Fri) to today, skipping weekends.
  function addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  }

  function formatDeliveryDate() {
    const target = addBusinessDays(new Date(), BRAND.deliveryBusinessDays);
    return target.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  }

  // ---------- On-page lightbox for policy pages ----------

  const LIGHTBOX_CSS = `
    #poc-lightbox-overlay{
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(0, 17, 53, 0.5);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: clamp(1rem, 4vw, 4rem) 1rem;
      overflow-y: auto;
    }
    #poc-lightbox-panel{
      background: #fff;
      width: min(760px, 100%);
      max-height: 100%;
      border-radius: ${BRAND.radius};
      box-shadow: none;
      display: flex;
      flex-direction: column;
      font-family: ${BRAND.fontBody};
    }
    #poc-lightbox-header{
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid ${BRAND.colorBorder};
      flex: none;
    }
    #poc-lightbox-title{
      font-size: 1.05rem;
      font-family: ${BRAND.fontHeading};
      font-weight: ${BRAND.fontWeightHeading};
      color: ${BRAND.colorText};
      margin: 0;
      text-transform: ${BRAND.titleTextTransform};
      letter-spacing: ${BRAND.titleLetterSpacing};
    }
    #poc-lightbox-close{
      background: none;
      border: none;
      cursor: pointer;
      font-size: 1.4rem;
      line-height: 1;
      padding: .25rem .5rem;
      color: ${BRAND.colorText};
    }
    #poc-lightbox-body{
      padding: 1.25rem;
      overflow-y: auto;
    }
    #poc-lightbox-body.poc-lightbox-loading{
      color: ${BRAND.colorText};
      font-size: .95rem;
      padding: 2.5rem 1.25rem;
      text-align: center;
    }
  `;

  function injectLightboxStyle() {
    if (document.getElementById('poc-lightbox-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-lightbox-style';
    style.textContent = LIGHTBOX_CSS;
    document.head.appendChild(style);
  }

  // Reuses the same scannable layout as jockey-shipping-page.user.js in this
  // folder instead of showing the raw, un-styled fetched page inside the
  // lightbox. Kept as a literal copy (not a shared import) since these are
  // standalone userscripts with no build step to share modules between them;
  // the lightbox header already shows "Shipping options" as the title, so
  // the h1 is omitted here.
  const SHIPPING_LIGHTBOX_CSS = `
    .poc-ship{ font-size: 1rem; }
    .poc-ship, .poc-ship *{ box-sizing: border-box; }
    .poc-ship-facts{ display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; list-style: none; margin: 0 0 1.5rem; padding: 0; border: 1px solid ${BRAND.colorBorder}; border-radius: ${BRAND.radius}; overflow: hidden; background: ${BRAND.colorBorder}; }
    .poc-ship-facts li{ padding: 1rem; background: #fff; margin: 0; }
    .poc-ship-facts strong{ display: block; font-size: 1rem; }
    .poc-ship-facts span{ display: block; font-size: .85rem; opacity: .68; margin-top: .2rem; }
    .poc-ship-cols{ display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; }
    .poc-ship-col{ border: 1px solid ${BRAND.colorBorder}; border-radius: ${BRAND.radius}; padding: 1rem 1.25rem; }
    .poc-ship-col h3{ margin: 0 0 .75rem; font-size: 1.15rem; }
    .poc-ship-lead{ margin: 0 0 .5rem; font-size: 1rem; line-height: 1.5; }
    .poc-ship-note{ margin: .5rem 0 0; font-size: .9rem; opacity: .68; line-height: 1.5; }
    table.poc-ship-rates{ width: 100%; border-collapse: collapse; font-size: 1rem; }
    table.poc-ship-rates th, table.poc-ship-rates td{ text-align: left; padding: .6rem .3rem; border-bottom: 1px solid rgba(127,127,127,.22); }
    table.poc-ship-rates th{ font-family: ${BRAND.fontHeading}; font-weight: 400; font-size: .75rem; letter-spacing: 0; text-transform: none; opacity: .75; }
    table.poc-ship-rates tr:last-child td{ border-bottom: none; }
    @media (max-width: 480px){
      .poc-ship-facts{ grid-template-columns: 1fr; }
      table.poc-ship-rates{ display: block; overflow-x: auto; white-space: nowrap; }
    }
  `;

  const SHIPPING_LIGHTBOX_MARKUP = `
    <div class="poc-ship">
      <ul class="poc-ship-facts">
        <li><strong>Free over $79</strong><span>Standard shipping, no membership needed</span></li>
        <li><strong>Same-day dispatch</strong><span>Orders before 10am Mon-Fri (express, in-stock) &mdash; otherwise next business day</span></li>
        <li><strong>1-7 day delivery</strong><span>Via Australia Post E-Parcel</span></li>
        <li><strong>Signature required</strong><span>Choose an address someone can receive at</span></li>
      </ul>
      <div class="poc-ship-cols">
        <div class="poc-ship-col">
          <h3>Australia</h3>
          <table class="poc-ship-rates">
            <thead><tr><th scope="col">Service</th><th scope="col">Delivery time</th><th scope="col">Price</th></tr></thead>
            <tbody>
              <tr><td>Standard</td><td>1-7 business days</td><td>$12.00 (free over $79)</td></tr>
              <tr><td>Express</td><td>1-3 business days</td><td>$17.95</td></tr>
            </tbody>
          </table>
          <p class="poc-ship-note">Standard via Australia Post E-Parcel; Express via Australia Post Express Post (not recommended for remote locations).</p>
        </div>
        <div class="poc-ship-col">
          <h3>New Zealand</h3>
          <p class="poc-ship-lead">$15.00 AUD, 2-5 business days via DHL.</p>
          <p class="poc-ship-note">Russell Athletic AU ships to Australia and New Zealand only &mdash; no other international shipping. Signature required on delivery.</p>
        </div>
      </div>
    </div>
  `;

  function injectShippingLightboxStyleOnce() {
    if (document.getElementById('poc-ship-lightbox-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-ship-lightbox-style';
    style.textContent = SHIPPING_LIGHTBOX_CSS;
    document.head.appendChild(style);
  }

  let lightboxLastFocused = null;

  function closeLightbox() {
    const overlay = document.getElementById('poc-lightbox-overlay');
    if (!overlay) return;
    overlay.remove();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onLightboxKeydown);
    if (lightboxLastFocused && typeof lightboxLastFocused.focus === 'function') {
      lightboxLastFocused.focus();
    }
  }

  function onLightboxKeydown(e) {
    if (e.key === 'Escape') closeLightbox();
  }

  function openLightbox(url, title) {
    closeLightbox();
    lightboxLastFocused = document.activeElement;

    const overlay = document.createElement('div');
    overlay.id = 'poc-lightbox-overlay';
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) closeLightbox();
    });

    const panel = document.createElement('div');
    panel.id = 'poc-lightbox-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    const header = document.createElement('div');
    header.id = 'poc-lightbox-header';
    const heading = document.createElement('h2');
    heading.id = 'poc-lightbox-title';
    heading.textContent = title;
    const closeBtn = document.createElement('button');
    closeBtn.id = 'poc-lightbox-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', closeLightbox);
    header.appendChild(heading);
    header.appendChild(closeBtn);

    const body = document.createElement('div');
    body.id = 'poc-lightbox-body';
    body.className = 'poc-lightbox-loading';
    body.textContent = 'Loading…';

    panel.appendChild(header);
    panel.appendChild(body);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onLightboxKeydown);
    closeBtn.focus();

    if (url === BRAND.shippingOptionsUrl) {
      injectShippingLightboxStyleOnce();
      body.className = '';
      body.innerHTML = SHIPPING_LIGHTBOX_MARKUP;
      return;
    }

    fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.getElementById('MainContent') || doc.querySelector('main') || doc.body;
        content.querySelectorAll('script, style').forEach((el) => el.remove());

        body.className = '';
        body.innerHTML = '';
        body.appendChild(document.importNode(content, true));
      })
      .catch(() => {
        body.className = '';
        body.innerHTML = '';
        const fallback = document.createElement('p');
        const link = document.createElement('a');
        link.href = url;
        link.textContent = 'View this page';
        fallback.append('Sorry, we couldn’t load this here. ', link, '.');
        body.appendChild(fallback);
      });
  }

  function interceptAsLightbox(link, title) {
    link.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openLightbox(link.href, title);
    });
  }

  // ---------- Shipping & Returns accordion copy ----------

  function rewriteShippingReturnsPanel() {
    if (document.getElementById('poc-shipping-returns-copy')) return;

    // RA's live theme splits Shipping and Returns into two separate <details>
    // accordion items (summaries "SHIPPING" / "EASY RETURNS"), each with its
    // own .accordion__content panel — not Jockey's single combined "Shipping
    // & Returns" item with a .cc-accordion-item__panel. Verified live 2026-09-18.
    function findPanelByHeading(pattern) {
      for (const d of document.querySelectorAll('details')) {
        const summary = d.querySelector('summary');
        if (summary && pattern.test((summary.textContent || '').trim())) {
          return d.querySelector('.accordion__content');
        }
      }
      return null;
    }

    const shippingPanel = findPanelByHeading(/^shipping$/i);
    const returnsPanel = findPanelByHeading(/returns/i);
    if (!shippingPanel && !returnsPanel) return;

    if (shippingPanel) {
      shippingPanel.innerHTML = '';
      shippingPanel.id = 'poc-shipping-returns-copy';

      const shippingP = document.createElement('p');
      const shippingLink = document.createElement('a');
      shippingLink.href = BRAND.shippingOptionsUrl;
      shippingLink.textContent = 'shipping options';
      interceptAsLightbox(shippingLink, 'Shipping options');
      shippingP.append(
        `Free standard shipping on all orders over ${BRAND.freeShippingThreshold} (no membership needed). See our full `,
        shippingLink,
        ' for details.'
      );
      shippingPanel.appendChild(shippingP);
    }

    if (returnsPanel) {
      returnsPanel.innerHTML = '';
      if (!shippingPanel) returnsPanel.id = 'poc-shipping-returns-copy';

      const returnsP = document.createElement('p');
      const returnsLink = document.createElement('a');
      returnsLink.href = BRAND.returnsPolicyUrl;
      returnsLink.textContent = 'returns and exchange policy';
      interceptAsLightbox(returnsLink, 'Returns & exchanges');
      returnsP.append(
        `Change your mind within ${BRAND.returnsWindowDays} days of delivery — items must be unworn with tags attached. Read our full `,
        returnsLink,
        '.'
      );
      returnsPanel.appendChild(returnsP);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
